import React from 'react'
import {
  analyticsField,
  analyticsDefaults,
  makeAnalyticsHandler,
  AnalyticsProps,
} from '../../fields/AnalyticsField'
import { Link } from 'gatsby'
import type { ComponentConfig } from '@puckeditor/core'
import { WithLayout, withLayout } from '../../components/Layout'
import { ColorField } from '../../fields/ColorField'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  FontWeight,
  TypographyProps,
  fontWeightOptions,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  applyTypography,
} from '../../fields/StyleFields'
import { Button as MuiButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import type { ButtonProps as _ButtonProps } from '@mui/material'
import { Icon, iconField } from '../../fields/IconField'

const ThemedButton = styled(MuiButton)({
  '&.MuiButton-contained': {
    backgroundColor: 'var(--theme-color-button)',
    '&:hover': {
      backgroundColor: 'var(--theme-color-button)',
      filter: 'brightness(0.85)',
    },
  },
  '&.MuiButton-outlined': {
    color: 'var(--theme-color-button)',
    borderColor: 'var(--theme-color-button)',
    '&:hover': {
      borderColor: 'var(--theme-color-button)',
    },
  },
  '&.MuiButton-text': {
    color: 'var(--theme-color-button)',
  },
})

export type ButtonVariant = NonNullable<_ButtonProps['variant']> | 'custom'

export type ButtonProps = WithLayout<{
  label: string
  variant: ButtonVariant
  size: _ButtonProps['size']
  fullWidth: boolean
  disableElevation: boolean
  textColor?: string
  fontSize?: string
  fontWeight?: FontWeight
  letterSpacing?: string
  lineHeight?: string
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  textTransform?: TypographyProps['textTransform']
  leftIcon?: string
  rightIcon?: string
  iconSize?: string
  linkType: 'none' | 'internal' | 'external' | 'script'
  href?: string
  openInNewTab?: boolean
  onClickScript?: string
  /** BR-5583: emit an analytics event when this button is clicked. */
  analytics?: AnalyticsProps
  background?: BackgroundStyleProps
  border?: BorderStyleProps
}>

const allFields: ComponentConfig<ButtonProps>['fields'] = {
  label: {
    label: 'Label',
    type: 'text',
    placeholder: 'Lorem ipsum...',
    contentEditable: true,
  },
  variant: {
    label: 'Variant',
    type: 'radio',
    options: [
      { label: 'Text', value: 'text' },
      { label: 'Outlined', value: 'outlined' },
      { label: 'Contained', value: 'contained' },
      { label: 'Custom', value: 'custom' },
    ],
  },
  size: {
    label: 'Size',
    type: 'radio',
    options: [
      { label: 'Small', value: 'small' },
      { label: 'Medium', value: 'medium' },
      { label: 'Large', value: 'large' },
    ],
  },
  fullWidth: {
    label: 'Full Width',
    type: 'radio',
    options: [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
  },
  disableElevation: {
    label: 'Disable Elevation',
    type: 'radio',
    options: [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
  },
  fontSize: {
    label: 'Font Size',
    type: 'text',
    placeholder: '16px',
  },
  fontWeight: {
    label: 'Font Weight',
    type: 'select',
    options: fontWeightOptions,
  },
  letterSpacing: {
    label: 'Letter Spacing',
    type: 'text',
    placeholder: '1px / 0.1em / 10%',
  },
  lineHeight: {
    label: 'Line Height',
    type: 'text',
    placeholder: '1.5 / 24px / 150%',
  },
  fontFamily: fontFamilyField,
  fontFamilyFallback: fontFamilyFallbackField,
  textTransform: textTransformField,
  leftIcon: { ...iconField, label: 'Left Icon' },
  rightIcon: { ...iconField, label: 'Right Icon' },
  iconSize: { type: 'text', label: 'Icon Size', placeholder: '20px' },
  textColor: {
    label: 'Text Color',
    type: 'custom',
    render: ({ value, onChange, id }) => (
      <ColorField
        field={{ type: 'colorPicker', label: 'Text Color' }}
        value={value as string}
        onChange={onChange}
        id={id}
        clearable
      />
    ),
  },
  linkType: {
    label: 'Link Type',
    type: 'radio',
    options: [
      { label: 'None', value: 'none' },
      { label: 'Internal', value: 'internal' },
      { label: 'External', value: 'external' },
      { label: 'JavaScript', value: 'script' },
    ],
  },
  href: {
    label: 'URL',
    type: 'text',
    placeholder: '/page or https://...',
  },
  openInNewTab: {
    label: 'Open in New Tab',
    type: 'radio',
    options: [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
  },
  onClickScript: {
    label: 'JavaScript (onClick)',
    type: 'textarea',
    placeholder: 'console.log("clicked")',
  },
  analytics: analyticsField,
}

const buildBaseFields = (variant: ButtonVariant) => {
  const base: Record<string, any> = {
    label: allFields.label,
    variant: allFields.variant,
    size: allFields.size,
    fullWidth: allFields.fullWidth,
    // Near the top on purpose: whether a button is tracked is a content decision, made
    // when the button is created, not a styling detail to hunt for at the bottom of the
    // panel. Independent of variant and linkType, so it sits in the shared base.
    analytics: (allFields as any).analytics,
  }
  if (variant === 'custom') {
    base.textColor = allFields.textColor
  } else {
    base.disableElevation = allFields.disableElevation
  }
  base.fontSize = allFields.fontSize
  base.fontWeight = allFields.fontWeight
  base.letterSpacing = allFields.letterSpacing
  base.lineHeight = allFields.lineHeight
  base.fontFamily = (allFields as any).fontFamily
  base.fontFamilyFallback = (allFields as any).fontFamilyFallback
  base.textTransform = (allFields as any).textTransform
  base.leftIcon = (allFields as any).leftIcon
  base.rightIcon = (allFields as any).rightIcon
  base.iconSize = (allFields as any).iconSize
  base.linkType = allFields.linkType
  return base
}


const ButtonInternal: ComponentConfig<ButtonProps> = {
  label: 'Button',
  // What the editor's agent reads about this block (AgentComponentNotes).
  ai: {
    description:
      'Call-to-action button; links when linkType is internal (a path on this site) or external (a full URL).',
    fields: {
      variant:
        'contained = filled, outlined, text = link style; custom = own colours through background and textColor.',
      linkType:
        'none, internal (/rewards), external (https://...), or script (runs JavaScript the person writes; you cannot set it).',
      leftIcon: 'Icon value such as lucide:ArrowRight; empty for none.',
      rightIcon: 'Icon value such as lucide:ArrowRight; empty for none.',
      analytics: 'Click tracking; leave it alone unless asked.',
    },
    // Run with new Function() on click outside the editor: only a person sets it.
    rawCodeFields: ['onClickScript'],
  },
  fields: allFields,
  defaultProps: {
    label: 'Button',
    variant: 'contained',
    size: 'medium',
    fullWidth: false,
    disableElevation: true,
    textColor: '',
    fontSize: '',
    fontWeight: '',
    letterSpacing: '',
    lineHeight: '',
    leftIcon: '',
    rightIcon: '',
    iconSize: '',
    linkType: 'none',
    href: '',
    openInNewTab: false,
    onClickScript: '',
    analytics: analyticsDefaults,
  },
  resolveFields: (data) => {
    const base = buildBaseFields(data.props.variant)
    if (data.props.linkType === 'internal' || data.props.linkType === 'external') {
      return { ...base, href: allFields.href, openInNewTab: (allFields as any).openInNewTab } as ComponentConfig<ButtonProps>['fields']
    }
    if (data.props.linkType === 'script') {
      return { ...base, onClickScript: allFields.onClickScript } as ComponentConfig<ButtonProps>['fields']
    }
    return base as ComponentConfig<ButtonProps>['fields']
  },
  render: ({
    label,
    variant,
    size,
    fullWidth,
    disableElevation,
    textColor,
    fontSize,
    fontWeight,
    letterSpacing,
    lineHeight,
    fontFamily,
    fontFamilyFallback,
    textTransform,
    leftIcon,
    rightIcon,
    iconSize,
    linkType,
    href,
    openInNewTab,
    onClickScript,
    analytics: analyticsProps,
    layout,
    puck,
  }) => {
    const leftIconEl = leftIcon ? (
      <Icon value={leftIcon} size={iconSize || 20} />
    ) : undefined
    const rightIconEl = rightIcon ? (
      <Icon value={rightIcon} size={iconSize || 20} />
    ) : undefined

    const runScript =
      linkType === 'script' && onClickScript && !puck.isEditing
        ? () => {
            try {
              new Function(onClickScript)()
            } catch (e) {
              console.error('[Button onClick]', e)
            }
          }
        : undefined

    // Tracking is deliberately independent of linkType: a button that navigates is
    // exactly the one worth measuring, and the old handler only existed for `script`.
    const emitEvent = puck.isEditing
      ? undefined
      : makeAnalyticsHandler(analyticsProps, label)

    const handleClick =
      runScript || emitEvent
        ? () => {
            emitEvent?.()
            runScript?.()
          }
        : undefined

    let button: React.ReactElement

    const fontWeightValue = fontWeight ? Number(fontWeight) || undefined : undefined
    const typographyStyle = applyTypography({ fontFamily, fontFamilyFallback, textTransform })

    const padCss = (v?: string) => {
      if (!v || v === '0') return undefined
      if (/^\d+(\.\d+)?$/.test(v)) return `${v}px`
      return v
    }
    const paddingStyle: React.CSSProperties = {
      paddingTop: padCss(layout?.padding?.top),
      paddingRight: padCss(layout?.padding?.right),
      paddingBottom: padCss(layout?.padding?.bottom),
      paddingLeft: padCss(layout?.padding?.left),
    }

    if (variant === 'custom') {
      const customStyle: React.CSSProperties = {
        background: 'transparent',
        border: 'none',
        padding: 0,
        color: textColor || undefined,
        width: fullWidth ? '100%' : undefined,
        cursor: 'pointer',
        font: 'inherit',
        fontSize: fontSize || undefined,
        fontWeight: fontWeightValue,
        letterSpacing: letterSpacing || undefined,
        lineHeight: lineHeight || undefined,
        // align icon(s) with the label
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: leftIconEl || rightIconEl ? 8 : undefined,
        ...typographyStyle,
        ...paddingStyle,
      }
      button = (
        <button
          type="button"
          style={customStyle}
          onClick={handleClick}
          tabIndex={puck.isEditing ? -1 : undefined}
        >
          {leftIconEl}
          {label}
          {rightIconEl}
        </button>
      )
    } else {
      button = (
        <ThemedButton
          variant={variant as _ButtonProps['variant']}
          size={size}
          fullWidth={fullWidth}
          disableElevation={disableElevation}
          onClick={handleClick}
          tabIndex={puck.isEditing ? -1 : undefined}
          startIcon={leftIconEl}
          endIcon={rightIconEl}
          style={{
            fontSize: fontSize || undefined,
            fontWeight: fontWeightValue,
            letterSpacing: letterSpacing || undefined,
            lineHeight: lineHeight || undefined,
            ...typographyStyle,
            ...paddingStyle,
          }}
        >
          {label}
        </ThemedButton>
      )
    }

    if (puck.isEditing || !href) return <>{button}</>
    if (linkType === 'internal') {
      if (openInNewTab) {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {button}
          </a>
        )
      }
      return <Link to={href as string}><>{button}</></Link>
    }
    if (linkType === 'external') {
      return (
        <a
          href={href}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
        >
          {button}
        </a>
      )
    }
    return <>{button}</>
  },
}

export const Button = withLayout(ButtonInternal, { disableWrapperPadding: true })
